import { getURLPrefix } from "utils/getUrlPrefix";
import { getAdTypeName } from "utils/getAdTypeName";
import { Badge } from "react-bootstrap";
import TrellisLink from "components/core/basic/TrellisLink";
import Flags from "country-flag-icons/react/3x2";
import { displayAdStatus } from "utils/displayAdStatus";
import { useAppSelector } from "redux/hooks";

const URL_PREFIX = getURLPrefix();

const SearchResult = ({ data, closeModal = () => {} }) => {
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);
  const orgIsWalmartVendor = channel.currentChannel === "walmart" && user.organization_type === "vendor";

  const result = data ?? {};
  const { org } = data;

  const {
    id,
    type,
    leaf_category_name,
    ad_count,
    product_count,
    name,
    ad_type,
    ad_status,
    state,
    product_title,
    asin,
    sku,
    plan_name,
    category,
    products,
    product,
    tags,
  } = result ?? {};

  let flagNationCode = result.market_place ?? result.marketplace;

  if (flagNationCode === "USA") {
    flagNationCode = "US";
  }

  if (flagNationCode === "UK") {
    flagNationCode = "GB";
  }

  const Flag = Flags[flagNationCode];

  const getResultContentByType = () => {
    let title = "";
    let resultURL = "";
    let imageURLParams = "";

    let subTitle = "";
    let tag = "";
    let customTags = [];
    let status = "";
    const state = "";

    switch (type) {
      case "category":
        title = leaf_category_name;

        resultURL =
          orgIsWalmartVendor ? `/user/advertising/dashboard/category/${id}` : `/user/merchandising/category/${id}`;
        imageURLParams = `category=${id}`;
        subTitle += `${ad_count} Media Plans & ${product_count} Products`;
        tag = "Category";
        break;
      case "ad":
        title = name;
        subTitle = getAdTypeName(ad_type);
        imageURLParams = `advertisement=${id}`;
        resultURL = `/user/advertising/dashboard/ads/${id}`;
        tag = "Media Plan";
        customTags = tags ?? [];
        status = ad_status;
        break;
      case "product":
        title = product_title;
        resultURL = `/user/merchandising/product/${id}`;
        imageURLParams = `product=${id}`;
        subTitle = `${asin} (${sku})`;
        tag = "Product";
        customTags = tags ?? [];
        break;
      case "pricingPlan":
        title = plan_name;
        resultURL = `/user/_pricing/plan/${id}`;
        imageURLParams = `pricingplan=${id}`;
        tag = "Pricing Plan";
        break;
      case "digitalShelf":
        title = name;
        resultURL = `/user/searchanalytics`;
        imageURLParams = `category=${category}`;
        tag = "Digital Shelf";
        break;
      case "productLaunch":
        title = name;
        resultURL = `/user/productlaunch/launches/${id}`;
        imageURLParams = `product=${products[0]}`;
        subTitle = state;
        tag = "Product Launch";
        break;
      case "productContent":
        title = result.product.product_title;
        resultURL = `/user/content/seo?id=${product.id}`;
        imageURLParams = `product=${product.id}`;
        subTitle = `${product.asin} (${product.sku})`;
        tag = "Product Content";
        break;
      case "productContentTemplate":
        title = name;
        resultURL = `/user/content/templates/${result.id}`;
        imageURLParams = `product=${products?.[0]?.id}`;
        tag = "Content Template";
        break;
      default:
        break;
    }

    return {
      title,
      subTitle,
      resultURL,
      imageURLParams,
      tag,
      status,
      customTags,
    };
  };

  const { imageURLParams, resultURL, subTitle, title, tag, status, customTags } = getResultContentByType();
  const image = `${URL_PREFIX}/api/productimage/?${imageURLParams}`;

  return (
    <TrellisLink
      to={resultURL}
      onClick={() => {
        if (resultURL === window.location.pathname) {
          closeModal();
        }
      }}
    >
      <div className="d-flex align-items-center border-top text-decoration-none">
        <img
          // Note: loading="lazy" must come before src for some browsers (i.e. Firefox)
          loading="lazy"
          src={image}
          height="45"
          width="45"
          alt={title}
          className="mx-3"
          onError={(e) => {
            e.currentTarget.src =
              "data:image/png;base64,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";
          }}
          style={{ objectFit: "cover" }}
        />

        <div className="p-4 " style={{ textAlign: "left" }}>
          <h2
            className="my-0 pe-5"
            style={{
              fontWeight: 500,
              textDecoration: "none",
              fontSize: "15px",
              color: "#2E0054",
            }}
          >
            {title}
          </h2>
          {subTitle && (
            <small
              className="text-nowrap pe-3"
              style={{
                fontWeight: "light",
                color: "#666",
                fontSize: "1rem",
                textDecoration: "none",
              }}
            >
              {subTitle} {status ? `- ${displayAdStatus(state, ad_status)}` : ""}
            </small>
          )}

          <div className="d-block">
            <Badge bsClass="badge" className="bg-trellis-purple me-3 my-1" pill>
              {tag}
            </Badge>
            {customTags?.map((tag, index) => (
              <Badge
                bsClass="badge"
                className="bg-trellis-mauve"
                style={{
                  marginRight: "1rem",
                  cursor: "pointer",
                }}
                pill
                key={index}
              >
                {tag}
              </Badge>
            ))}
          </div>
          <div>
            {org?.profile_name}
            {flagNationCode && <Flag style={{ width: "18px" }} className="ms-3" />}
          </div>
        </div>
      </div>
    </TrellisLink>
  );
};

export default SearchResult;
